import { Box, Paper, useMediaQuery } from "@material-ui/core";
import Highcharts from "highcharts";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import CssGroupButton from "src/components/Button/CssGroupButton";
import { STAKING_TYPE } from "src/components/ScoringChart/Stack/config";
import StackChart from "src/components/ScoringChart/Stack/StackChart";
import { baseBeautifulTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import DateGroupButton from "src/components/ScoringChart/tool-utils/DateGroupButton";
import { NoteTypography } from "src/components/Typography/TitleTypography";
import DetailMonitoringWrapper from "src/components/Wrapper/DetailMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import {
  useAnalysisOverviewChartSelector,
  useAnalysisOverviewSelector,
} from "src/context/monitoring/analysisOverviewContext";

const dateOption = [
  { label: "1D", number: 24, interval: 1 },
  { label: "1W", number: 84, interval: 2 },
  { label: "1M", number: 90, interval: 8 },
  { label: "3M", number: 180, interval: 12 },
];

const getOption = () => {
  return {
    chart: {
      height: 500,
    },
    legend: {
      enabled: true,
    },
    ...baseBeautifulTooltip(
      (value) => value,
      true,
      (value) => {
        const times = value.split("-");
        let time1 = times[0].length === 0 ? null : Highcharts.dateFormat("%b %e, %Y, %k:%M", Number(times[0]));
        let time2 = Highcharts.dateFormat("%b %e, %Y, %k:%M", Number(times[1]));
        let result = time1 ? `<div>From: ${time1}</div>` : "";
        result += `<div>To: ${time2}</div>`;
        return result;
      }
    ),
    xAxis: {
      labels: {
        enabled: true,
        formatter: function () {
          const times = this.value.split("-");
          let time2 = Highcharts.dateFormat("%e.%b", Number(times[1]));
          return time2;
        },
      },
    },
  };
};

export default function NumberOfTransactions() {
  const { pool } = useParams();
  const match = useMediaQuery((theme) => theme.breakpoints.only("xs"));
  const { fetchNumberOfTransactions } = useAnalysisOverviewSelector((state) => state.fetch);
  const numberOfTransactions = useAnalysisOverviewChartSelector(MONITORING.OVERVIEW.numberOfTransactions, pool);
  const [stakingType, setStakingType] = useState(STAKING_TYPE.PERCENT);

  useEffect(() => {
    if (numberOfTransactions?.active === undefined)
      fetchNumberOfTransactions(pool, { number: 90, interval: 8 }, { active: 2 });
    else fetchNumberOfTransactions(pool, numberOfTransactions?.metadata, { active: numberOfTransactions?.active });
  }, [numberOfTransactions?.active, pool]);

  const { categories, series, isLoading } = useMemo(() => {
    if (numberOfTransactions) {
      return {
        isLoading: false,
        categories: [].concat(numberOfTransactions["timestamps"]),
        series: [
          {
            name: "deposit",
            data: numberOfTransactions["deposit"].map((element) => {
              return [].concat(element);
            }),
          },
          {
            name: "borrow",
            data: numberOfTransactions["borrow"].map((element) => {
              return [].concat(element);
            }),
          },
          {
            name: "withdraw",
            data: numberOfTransactions["withdraw"].map((element) => {
              return [].concat(element);
            }),
          },
          {
            name: "repay",
            data: numberOfTransactions["repay"].map((element) => {
              return [].concat(element);
            }),
          },
        ],
      };
    }
    return {
      isLoading: true,
      categories: [],
      series: [],
    };
  }, [numberOfTransactions]);

  return (
    <DetailMonitoringWrapper
      navigation={{ text: "overview", link: `/overview-analysis/${pool}`, name: "number of transaction" }}
      selectLink="/overview-analysis/number-of-transactions"
      Component={{
        Description: (
          <>
            <NoteTypography>
              The chart shows the daily number of Deposit, Borrow, Withdraw, and Repay transactions in the selected
              lending pool. This shows the status of the lending pool, including the activeness and transaction trends.
              For instance, a significant increase in the number of depositing transactions (compared to 3 others) over
              a particular period shows that users tend to deposit their assets in the lending pool.
            </NoteTypography>
            <NoteTypography>
              Users can check the absolute number of transactions to monitor the activeness of the lending market, or
              they can use the percentage unit to observe the dominated type of transactions.
            </NoteTypography>
          </>
        ),
      }}
    >
      <Paper>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2}>
          <CssGroupButton
            fullWidth={match}
            style={match ? { marginBottom: "0.5rem" } : {}}
            defaultActive={stakingType === STAKING_TYPE.PERCENT ? 0 : 1}
            config={[
              { label: "Percentage(%)", onClick: () => setStakingType(STAKING_TYPE.PERCENT) },
              { label: "Value", onClick: () => setStakingType(STAKING_TYPE.NORMAL) },
            ]}
          />
          <DateGroupButton
            fullWidth={match}
            defaultActive={numberOfTransactions?.active ?? 0}
            picker={{
              continueClick: (from, to, active) => {
                fetchNumberOfTransactions(pool, { from, to }, { active });
              },
            }}
            config={dateOption.map((item, _) => ({
              data: item,
              onClick: (data, active) => {
                fetchNumberOfTransactions(pool, { number: data.number, interval: data.interval }, { active });
              },
            }))}
          />
        </Box>
        <StackChart
          isLoading={isLoading}
          type={stakingType}
          series={series}
          categories={categories}
          option={getOption()}
        />
      </Paper>
    </DetailMonitoringWrapper>
  );
}
